import { useCallback, useRef, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { mapCardTypeToLoyalty } from 'features/payment/utils';

import { CartPaymentCardType, LoyaltyPromotionType } from 'generated/graphql-gateway';
import { actions, useAppDispatch } from 'state/global-state';
import { useLoyaltyIncentivesEvaluation } from 'state/loyalty/hooks/use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useServiceModeContext } from 'state/service-mode';

import { ICartEntry } from '../types';
import {
  calculateCartSubTotal,
  getLoyaltyAppliedIncentives,
  hasOffers,
  hasRewards,
  isRewardCartEntry,
  mapCartEntriesToLoyaltyCartEntries,
} from '../utils';

/**
 * Specifically designed to be used for minimal cart entry evaluation
 * It will only evaluate the offers and rewards that are applied to the cart entries
 *
 * - In order to decouple this from the checkout in the future, we may need a different hook for this
 * - TODO: explore the possibility of using the useLoyaltyIncentivesEvaluation hook directly with a serverOrder
 * @returns
 */
export const useCartIncentivesEvaluation = ({ cartEntries }: { cartEntries: ICartEntry[] }) => {
  const { evaluateLoyaltyOffers, evaluateLoyaltyRewards } = useLoyaltyIncentivesEvaluation();
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestIdRef = useRef(0);
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { serviceMode } = useServiceModeContext();
  const { checkoutPaymentMethod } = usePaymentContext();
  const paymentMethod = mapCardTypeToLoyalty(
    checkoutPaymentMethod?.credit?.cardType as CartPaymentCardType
  );
  const dispatch = useAppDispatch();

  const evaluateCartIncentives = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;

    if (!loyaltyId) {
      return;
    }
    const commonParams = {
      loyaltyId,
      serviceMode,
      subtotalAmount: calculateCartSubTotal(cartEntries),
      paymentMethod,
    };
    try {
      setIsLoading(true);
      // Only evaluate the offers and rewards that are applied to the entries that affects the subtotal
      const mainCartEntries = cartEntries.filter(entry => !isRewardCartEntry(entry));
      if (hasOffers(cartEntries)) {
        const result = await evaluateLoyaltyOffers({
          ...commonParams,
          appliedIncentives: getLoyaltyAppliedIncentives({
            cartEntries,
            incentiveType: LoyaltyPromotionType.OFFER,
          }),
          cartEntries: mapCartEntriesToLoyaltyCartEntries(
            mainCartEntries,
            LoyaltyPromotionType.OFFER
          ),
        });

        if (result && requestId === latestRequestIdRef.current) {
          dispatch(actions.loyalty.setOffersFeedbackMap(result));
        }
      }
      if (hasRewards(cartEntries)) {
        const result = await evaluateLoyaltyRewards({
          ...commonParams,
          appliedIncentives: getLoyaltyAppliedIncentives({
            cartEntries,
            incentiveType: LoyaltyPromotionType.REWARD,
          }),
          cartEntries: mapCartEntriesToLoyaltyCartEntries(
            mainCartEntries,
            LoyaltyPromotionType.REWARD
          ),
        });
        if (result && requestId === latestRequestIdRef.current) {
          dispatch(actions.loyalty.setRewardsFeedbackMap(result));
        }
      }
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    loyaltyId,
    serviceMode,
    cartEntries,
    paymentMethod,
    evaluateLoyaltyOffers,
    dispatch,
    evaluateLoyaltyRewards,
  ]);

  return { isLoading, evaluateCartIncentives } as const;
};
